export enum tripReasons {
	Negocio = 'Negocio',
	Grupo = 'Grupo',
	Proyecto = 'Proyecto',
	Otro = 'Otro',
	// Para quien reserva sin un motivo que declarar. Se guarda tal cual, así
	// que el registro dice «sin motivo» en vez de quedar vacío o, como antes,
	// con un 'business' que nadie eligió.
	SinMotivo = 'Sin motivo de viaje',
}

// Selección del selector de motivo cuando hay catálogo de empresa: `reason:<id>`
// para un motivo del catálogo, o estas dos llaves para «Otro» y «Sin motivo».
export const TRIP_REASON_OTHER = 'other'
export const TRIP_REASON_NONE = 'none'

export const tripReasonKey = (id: number) => `reason:${id}`

export const tripReasonIdFromKey = (key: string): number | null => {
	const match = /^reason:(\d+)$/.exec(key)
	return match ? Number(match[1]) : null
}

// Ícono por categoría estándar del catálogo (BookingV2 `StandardTripReason`).
export const tripReasonCategoryIcons: Record<string, string> = {
	business: 'work',
	project: 'assignment',
	group: 'groups',
	training: 'school',
	client_visit: 'handshake',
	event: 'event',
	audit: 'fact_check',
	technical_support: 'build',
	internal_meeting: 'forum',
}

interface TripReasonEntry {
	value: string
	label: tripReasons
}

// Diccionario de motivos que pueden venir guardados en inglés. Antes del
// selector, BookingV2 guardaba 'business' fijo en toda reserva de hotel; esas
// reservas viejas siguen leyéndose «Negocio» aunque nadie lo eligiera.
export const tripReasonDictionary: readonly TripReasonEntry[] = [
	{ value: 'business', label: tripReasons.Negocio },
	{ value: 'group', label: tripReasons.Grupo },
	{ value: 'project', label: tripReasons.Proyecto },
	{ value: 'other', label: tripReasons.Otro },
]

// Texto en español del motivo guardado. Si el valor no está en el diccionario
// es el texto libre que capturaron en «Otro» (o ya viene en español) y se
// muestra tal cual.
export function tripReasonLabel(reason: string | null | undefined): string {
	const value = reason?.trim()
	if (!value) return ''

	const entry = tripReasonDictionary.find(
		(item) => item.value === value.toLowerCase()
	)

	return entry?.label ?? value
}

/**
 * Campos de motivo para el payload de una solicitud a partir de la llave del
 * selector: un motivo del catálogo viaja como `tripReasonId`, «Otro» como
 * `reasonOther` (y su texto también en `reason`) y, en el modo de opciones fijas, el texto como `reason` (BookingV2
 * lo liga al catálogo si coincide). «Sin motivo de viaje» no manda nada.
 */
export function tripReasonPayload(
	key: string,
	other = ''
): { tripReasonId?: number; reasonOther?: string; reason?: string } {
	const id = tripReasonIdFromKey(key)
	if (id !== null) return { tripReasonId: id }
	if (key === TRIP_REASON_OTHER || key === tripReasons.Otro) {
		// También en `reason`: un BookingV2 que todavía no conozca `reasonOther`
		// guarda el texto en vez de dejar la reserva sin motivo.
		const text = other.trim()
		return { reasonOther: text, reason: text }
	}
	if (!key || key === TRIP_REASON_NONE) return {}

	return { reason: key }
}

/** ¿La llave pide escribir el motivo y todavía no se escribió? */
export function tripReasonMissingOther(key: string, other = ''): boolean {
	return (
		(key === TRIP_REASON_OTHER || key === tripReasons.Otro) && !other.trim()
	)
}
